import React, { useRef, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useLocation } from 'react-router-dom';
import ExcelReaderService from '../../services/ExcelReaderService';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import moment from 'moment';
import { toString } from 'lodash';
import AuthService from '../../services/AuthService';
import ToastService from '../../services/ToastService';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const HIGHLIGHT_COLS = ['full_name', 'phone_number', 'email', 'city', 'platform'];

const styles = {
  pageWrapper: {
    padding: '24px',
    minHeight: '100vh',
    background: '#f4f6fb',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '28px',
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '20px',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(52,71,103,0.3)',
  },
  headerTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#344767',
    margin: 0,
    lineHeight: 1.2,
  },
  headerSub: {
    fontSize: '13px',
    color: '#8392ab',
    margin: 0,
    marginTop: '2px',
  },
  stepBar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '28px',
    background: '#fff',
    borderRadius: '16px',
    padding: '18px 28px',
    boxShadow: '0 2px 12px rgba(52,71,103,0.07)',
    gap: 0,
  },
  stepItem: (active, done) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
  }),
  stepCircle: (active, done) => ({
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
    flexShrink: 0,
    transition: 'all 0.3s',
    background: done
      ? 'linear-gradient(135deg, #2dce89, #26af74)'
      : active
      ? 'linear-gradient(135deg, #344767, #21243d)'
      : '#e9ecef',
    color: done || active ? '#fff' : '#8392ab',
    boxShadow: done || active ? '0 4px 10px rgba(52,71,103,0.25)' : 'none',
  }),
  stepLabel: (active, done) => ({
    fontSize: '13px',
    fontWeight: active || done ? '600' : '400',
    color: active || done ? '#344767' : '#8392ab',
    whiteSpace: 'nowrap',
  }),
  stepLine: (done) => ({
    flex: 1,
    height: '2px',
    background: done ? '#2dce89' : '#e9ecef',
    margin: '0 10px',
    borderRadius: '2px',
    transition: 'background 0.3s',
  }),
  uploadCard: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(52,71,103,0.07)',
    padding: '40px 32px',
    marginBottom: '24px',
  },
  dropZone: (dragging) => ({
    border: `2.5px dashed ${dragging ? '#344767' : '#c8d0e0'}`,
    borderRadius: '14px',
    padding: '48px 24px',
    textAlign: 'center',
    background: dragging ? '#f0f3ff' : '#f8f9fe',
    transition: 'all 0.25s',
    cursor: 'pointer',
    position: 'relative',
  }),
  dropIcon: {
    fontSize: '44px',
    color: '#8392ab',
    marginBottom: '14px',
    display: 'block',
  },
  dropTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#344767',
    marginBottom: '6px',
  },
  dropSub: {
    fontSize: '13px',
    color: '#8392ab',
    marginBottom: '20px',
  },
  browseBtn: {
    background: 'linear-gradient(135deg, #344767, #21243d)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(52,71,103,0.25)',
    transition: 'opacity 0.2s',
  },
  hiddenInput: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    cursor: 'pointer',
    width: '100%',
    height: '100%',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#f0f9f4',
    border: '1.5px solid #b2dfdb',
    borderRadius: '10px',
    padding: '12px 18px',
    marginTop: '18px',
  },
  fileIcon: {
    width: '38px',
    height: '38px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #2dce89, #26af74)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '16px',
    flexShrink: 0,
  },
  fileName: {
    fontWeight: '600',
    color: '#344767',
    fontSize: '14px',
    margin: 0,
  },
  fileSize: {
    color: '#8392ab',
    fontSize: '12px',
    margin: 0,
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  statCard: (color) => ({
    flex: '1',
    minWidth: '140px',
    background: '#fff',
    borderRadius: '14px',
    padding: '18px 20px',
    boxShadow: '0 2px 12px rgba(52,71,103,0.07)',
    borderLeft: `4px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  }),
  statIcon: (color) => ({
    width: '42px',
    height: '42px',
    borderRadius: '10px',
    background: color + '18',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: color,
    fontSize: '18px',
    flexShrink: 0,
  }),
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#344767',
    lineHeight: 1,
    marginBottom: '2px',
  },
  statLabel: {
    fontSize: '12px',
    color: '#8392ab',
    fontWeight: '500',
  },
  tableCard: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(52,71,103,0.07)',
    overflow: 'hidden',
    marginBottom: '24px',
  },
  tableHeader: {
    padding: '18px 24px',
    borderBottom: '1px solid #f0f2f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tableTitle: {
    fontWeight: '700',
    color: '#344767',
    fontSize: '15px',
    margin: 0,
  },
  tableBadge: {
    background: '#e8f5e9',
    color: '#2dce89',
    borderRadius: '20px',
    padding: '3px 12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  tableWrap: {
    overflowX: 'auto',
    maxHeight: '380px',
    overflowY: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    minWidth: '700px',
  },
  th: (highlighted) => ({
    padding: '11px 16px',
    background: '#f8f9fe',
    color: highlighted ? '#344767' : '#8392ab',
    fontWeight: highlighted ? '700' : '600',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    borderBottom: '2px solid #e9ecef',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  }),
  td: (highlighted) => ({
    padding: '10px 16px',
    borderBottom: '1px solid #f0f2f5',
    color: highlighted ? '#344767' : '#525f7f',
    fontWeight: highlighted ? '500' : '400',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  trEven: {
    background: '#fff',
  },
  trOdd: {
    background: '#fafbfc',
  },
  footerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#fff',
    borderRadius: '16px',
    padding: '18px 24px',
    boxShadow: '0 2px 12px rgba(52,71,103,0.07)',
    flexWrap: 'wrap',
    gap: '12px',
  },
  footerNote: {
    fontSize: '13px',
    color: '#8392ab',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  importBtn: (loading) => ({
    background: loading
      ? '#8392ab'
      : 'linear-gradient(135deg, #344767, #21243d)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 32px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: loading ? 'not-allowed' : 'pointer',
    boxShadow: loading ? 'none' : '0 4px 15px rgba(52,71,103,0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    letterSpacing: '0.02em',
  }),
  emptyState: {
    textAlign: 'center',
    padding: '52px 24px',
    color: '#8392ab',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '14px',
    display: 'block',
    opacity: 0.35,
  },
  emptyText: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#525f7f',
    margin: '0 0 6px',
  },
  emptySub: {
    fontSize: '13px',
    color: '#8392ab',
    margin: 0,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2.5px solid rgba(255,255,255,0.4)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
};

const ExcelReader = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const toast = useRef(null);
  const fileInputRef = useRef(null);
  const toastService = new ToastService(toast);
  const [fileSelected, setFileSelected] = useState(false);
  const queryParam = useQuery();
  const [UserId] = useState(() => AuthService.resolveScopedUserId(queryParam.get("id")));
  const clientId = AuthService.getLoggedInClientId();

  const step = !fileSelected ? 1 : data.length === 0 ? 1 : 2;

  const processFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    setFileSize((file.size / 1024).toFixed(1) + ' KB');
    setFileSelected(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary', cellDates: true });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const formattedData = jsonData.map((row) => {
        Object.keys(row).forEach((key) => {
          if (row[key] instanceof Date) {
            row[key] = moment(row[key]).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
          }
        });
        return row;
      });
      setData(formattedData);
    };
    reader.readAsBinaryString(file);
  };

  const handleFileUpload = (event) => {
    processFile(event.target.files[0]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      processFile(file);
    } else {
      toastService.showWarning("Invalid File", "Please drop a valid .xlsx or .xls file.");
    }
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const handleSubmit = async () => {
    if (!fileSelected) {
      toastService.showWarning("Warning", "Please select a file before importing.");
      return;
    }
    try {
      setLoading(true);
      const importLeads = data.map((row) => ({
        adId: toString(row.ad_id),
        adName: toString(row.ad_name),
        adsetId: toString(row.adset_id),
        adsetName: toString(row.adset_name),
        compaignId: toString(row.campaign_id),
        compaignName: toString(row.campaign_name),
        formId: toString(row.form_id),
        formName: toString(row.form_name),
        isOrganic: row.isOrganic,
        platform: toString(row.platform),
        interestedInSector115Mohali: toString(row['interested_in_buying_property_in_sector_115_mohali?'] ?? row.interested_in_buying_property_in_sector_115_mohali),
        budget: toString(row['what_is_your_budget?'] ?? row.what_is_your_budget),
        plannedPurchaseTimeline: toString(row.when_are_you_planning_to_purchase_the_property),
        whatsappNumber: toString(row.whatsapp),
        fullName: toString(row.full_name),
        email: toString(row.email),
        city: toString(row.city),
        phoneNumber: toString(row.phone_number),
        leadStatusId: row.leadStatusId,
        createdBy: 0,
        createdOn: new Date(),
        modifiedOn: new Date(),
        modifiedBy: 0,
        isActive: true,
        createdTime: row.created_time,
        clientId: clientId,
      }));
      await ExcelReaderService.createImportLead(importLeads, UserId);
      toastService.showSuccess("Success", "Leads imported successfully!");
      setData([]);
      setFileSelected(false);
      setFileName('');
      setFileSize('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toastService.showError("Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const colCount = data.length > 0 ? Object.keys(data[0]).length : 0;

  return (
    <div style={styles.pageWrapper}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Toast ref={toast} />

      {/* Page header */}
      <div style={styles.headerRow}>
        <div style={styles.headerIcon}>
          <i className="fas fa-file-import" />
        </div>
        <div>
          <p style={styles.headerTitle}>Import Leads</p>
          <p style={styles.headerSub}>Upload an Excel file to bulk-import leads into the system</p>
        </div>
      </div>

      {/* Step indicator */}
      <div style={styles.stepBar}>
        {[
          { num: 1, label: 'Upload File' },
          { num: 2, label: 'Preview Data' },
          { num: 3, label: 'Import' },
        ].map(({ num, label }, idx, arr) => {
          const done = step > num;
          const active = step === num;
          return (
            <React.Fragment key={num}>
              <div style={styles.stepItem(active, done)}>
                <div style={styles.stepCircle(active, done)}>
                  {done ? <i className="fas fa-check" style={{ fontSize: '13px' }} /> : num}
                </div>
                <span style={styles.stepLabel(active, done)}>{label}</span>
              </div>
              {idx < arr.length - 1 && (
                <div style={styles.stepLine(done)} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Upload card */}
      <div style={styles.uploadCard}>
        <div
          style={styles.dropZone(dragging)}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={styles.hiddenInput}
          />
          <i className="fas fa-cloud-upload-alt" style={styles.dropIcon} />
          <p style={styles.dropTitle}>
            {dragging ? 'Drop your file here' : 'Drag & drop your Excel file here'}
          </p>
          <p style={styles.dropSub}>Supported formats: .xlsx, .xls</p>
          <button
            style={styles.browseBtn}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            type="button"
          >
            <i className="fas fa-folder-open" style={{ marginRight: '7px' }} />
            Browse File
          </button>
        </div>

        {fileSelected && fileName && (
          <div style={styles.fileInfo}>
            <div style={styles.fileIcon}>
              <i className="fas fa-file-excel" />
            </div>
            <div>
              <p style={styles.fileName}>{fileName}</p>
              <p style={styles.fileSize}>{fileSize} &nbsp;·&nbsp; {data.length} rows detected</p>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <span style={{ ...styles.tableBadge, background: '#e3f2fd', color: '#1565c0' }}>
                <i className="fas fa-check-circle" style={{ marginRight: '5px' }} />
                Ready
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      {data.length > 0 && (
        <div style={styles.statsRow}>
          {[
            { label: 'Total Rows', value: data.length, color: '#344767', icon: 'fa-list-ol' },
            { label: 'Columns', value: colCount, color: '#5e72e4', icon: 'fa-columns' },
            { label: 'Ready to Import', value: data.length, color: '#2dce89', icon: 'fa-check-double' },
            { label: 'Skipped', value: 0, color: '#fb6340', icon: 'fa-ban' },
          ].map(({ label, value, color, icon }) => (
            <div key={label} style={styles.statCard(color)}>
              <div style={styles.statIcon(color)}>
                <i className={`fas ${icon}`} />
              </div>
              <div>
                <div style={styles.statValue}>{value}</div>
                <div style={styles.statLabel}>{label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview table */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <p style={styles.tableTitle}>
            <i className="fas fa-table" style={{ marginRight: '8px', color: '#8392ab' }} />
            Data Preview
          </p>
          {data.length > 0 && (
            <span style={styles.tableBadge}>{data.length} records</span>
          )}
        </div>

        {data.length === 0 ? (
          <div style={styles.emptyState}>
            <i className="fas fa-inbox" style={styles.emptyIcon} />
            <p style={styles.emptyText}>No data to display</p>
            <p style={styles.emptySub}>Upload an Excel file to preview leads before importing</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th(false)}>#</th>
                  {Object.keys(data[0]).map((h) => (
                    <th key={h} style={styles.th(HIGHLIGHT_COLS.includes(h))}>
                      {HIGHLIGHT_COLS.includes(h) && (
                        <i className="fas fa-star" style={{ fontSize: '9px', marginRight: '5px', color: '#5e72e4' }} />
                      )}
                      {h.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={{ ...styles.td(false), color: '#c0c8d8', fontWeight: '500' }}>{i + 1}</td>
                    {Object.keys(data[0]).map((h) => (
                      <td key={h} style={styles.td(HIGHLIGHT_COLS.includes(h))} title={String(row[h] ?? '')}>
                        {String(row[h] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action footer */}
      <div style={styles.footerBar}>
        <span style={styles.footerNote}>
          <i className="fas fa-info-circle" style={{ color: '#5e72e4' }} />
          {data.length > 0
            ? `${data.length} lead${data.length !== 1 ? 's' : ''} will be imported on submit`
            : 'Upload a file to get started'}
        </span>
        <button
          style={styles.importBtn(loading)}
          onClick={handleSubmit}
          disabled={loading}
          type="button"
        >
          {loading ? (
            <>
              <span style={styles.spinner} />
              Importing…
            </>
          ) : (
            <>
              <i className="fas fa-file-import" />
              Import Leads
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ExcelReader;
